export const aiCapabilities = [
  {
    icon: 'ri-brain-line',
    title: '智能能源优化',
    subtitle: 'AI 驱动的能耗预测与调度',
    description: '基于深度学习的能源消耗预测模型，实时分析设备运行数据，自动识别能耗异常模式，提供精准的节能优化方案。平均帮助企业降低 15-30% 能源成本。',
    metrics: [
      { label: '节能率', value: '15-30%' },
      { label: '预测准确率', value: '95%+' },
      { label: '响应速度', value: '<100ms' },
    ],
    highlights: ['负荷预测', '峰谷优化', '设备调度', '异常检测'],
  },
  {
    icon: 'ri-settings-3-line',
    title: '生产流程优化',
    subtitle: '工艺参数智能调优',
    description: '通过分析生产全流程数据，AI 模型自动识别工艺瓶颈，优化设备运行参数，提升生产效率与产品质量。支持多工序协同优化，实现产能最大化。',
    metrics: [
      { label: '效率提升', value: '20-40%' },
      { label: '良品率提升', value: '5-15%' },
      { label: '产能提升', value: '10-25%' },
    ],
    highlights: ['工艺优化', '参数调优', '瓶颈识别', '质量预测'],
  },
  {
    icon: 'ri-leaf-line',
    title: '碳排放智能管理',
    subtitle: '全链路碳足迹追踪与优化',
    description: 'AI 自动核算企业全链路碳排放数据，识别高碳排放环节，提供减排路径规划。支持碳资产管理和碳交易决策，助力企业实现碳中和目标。',
    metrics: [
      { label: '碳排降低', value: '20-35%' },
      { label: '核算精度', value: '99.2%' },
      { label: '报告生成', value: '1分钟' },
    ],
    highlights: ['碳足迹追踪', '减排规划', '碳资产管理', '合规报告'],
  },
  {
    icon: 'ri-tools-line',
    title: '预测性设备维护',
    subtitle: '故障预警与寿命预测',
    description: '基于设备运行数据的 AI 故障预测模型，提前 7-30 天预警设备故障风险，智能规划维护计划，减少非计划停机，延长设备使用寿命。',
    metrics: [
      { label: '故障预警', value: '7-30天' },
      { label: '停机减少', value: '60%+' },
      { label: '寿命延长', value: '15-25%' },
    ],
    highlights: ['故障预测', '寿命评估', '维护规划', '备件优化'],
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: '智能决策支持',
    subtitle: '数据驱动的经营决策',
    description: '整合企业能源、生产、碳排等多维数据，AI 生成可视化经营洞察报告，为管理层提供数据驱动的决策支持，实现精细化管理。',
    metrics: [
      { label: '决策效率', value: '提升50%' },
      { label: '数据整合', value: '20+维度' },
      { label: '报告生成', value: '实时' },
    ],
    highlights: ['经营洞察', '趋势预测', '对标分析', '智能报告'],
  },
  {
    icon: 'ri-shield-check-line',
    title: '安全与合规保障',
    subtitle: '智能合规监测与风险预警',
    description: 'AI 实时监测企业能源使用合规性，自动识别超标风险，预警潜在安全隐患。支持 ISO 50001、绿色工厂等多标准合规管理。',
    metrics: [
      { label: '合规率', value: '99.8%' },
      { label: '风险识别', value: '实时' },
      { label: '标准覆盖', value: '15+' },
    ],
    highlights: ['合规监测', '风险预警', '标准对标', '审计支持'],
  },
];

export const aiTechStack = [
  {
    layer: '应用层',
    items: ['能源优化引擎', '生产调度系统', '碳排管理平台', '智能报告中心'],
    color: 'from-firmai-green/20 to-firmai-green/5',
    borderColor: 'border-firmai-green/30',
  },
  {
    layer: '算法层',
    items: ['LSTM 时序预测', 'Transformer 异常检测', '强化学习调度', '图神经网络关联分析'],
    color: 'from-firmai-blue/20 to-firmai-blue/5',
    borderColor: 'border-firmai-blue/30',
  },
  {
    layer: '数据层',
    items: ['实时数据流处理', '多源数据融合', '特征工程平台', '知识图谱构建'],
    color: 'from-amber-500/20 to-amber-500/5',
    borderColor: 'border-amber-500/30',
  },
  {
    layer: '基础层',
    items: ['分布式计算集群', 'GPU 加速推理', '边缘计算节点', '安全数据沙箱'],
    color: 'from-rose-500/20 to-rose-500/5',
    borderColor: 'border-rose-500/30',
  },
];

export const aiResults = [
  {
    company: '华能精密制造',
    industry: '高端制造',
    metric: '年节约 860万',
    detail: '能源成本降低 32%，设备故障率下降 60%',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20business%20executive%20portrait%20headshot%20wearing%20suit%20confident%20smile%20clean%20background%20corporate%20photography&width=60&height=60&seq=aiavatar1&orientation=squarish',
    name: '张明华',
    title: '能源管理总监',
  },
  {
    company: '顺丰智慧物流',
    industry: '物流仓储',
    metric: '减排 5000吨/年',
    detail: '碳排放降低 28%，运输效率提升 15%',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20business%20executive%20portrait%20headshot%20wearing%20blazer%20confident%20smile%20clean%20background%20corporate%20photography&width=60&height=60&seq=aiavatar2&orientation=squarish',
    name: '李雪婷',
    title: '可持续发展负责人',
  },
  {
    company: '腾讯云数据中心',
    industry: '数据中心',
    metric: 'PUE 降至 1.25',
    detail: '冷却能耗降低 28%，年省 1200万',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20senior%20executive%20portrait%20headshot%20wearing%20glasses%20suit%20confident%20clean%20background%20corporate%20photography&width=60&height=60&seq=aiavatar3&orientation=squarish',
    name: '王建国',
    title: '运营副总裁',
  },
];

export const ccerIntegration = {
  title: '对接全国温室气体自愿减排注册登记系统',
  subtitle: 'CCER 系统数据直连',
  badge: 'CCER 认证支持',
  description:
    '支持工厂碳排放数据自动采集、标准化处理并直接对接全国温室气体自愿减排注册登记系统（CCER），助力企业完成 CCER 项目备案、减排量核算和注册申请全流程数字化。',
  stats: [
    { label: '数据采集类型', value: '20+', sub: '覆盖工业全场景' },
    { label: '核算方法学', value: '15+', sub: '符合 CCER 标准' },
    { label: '报告生成时间', value: '<5分钟', sub: '自动生成合规报告' },
    { label: '数据准确率', value: '99.5%+', sub: 'AI 校验双重保障' },
  ],
  dataTypes: [
    { icon: 'ri-fire-line', label: '燃料燃烧排放', desc: '天然气、燃油、煤炭等化石燃料消耗数据' },
    { icon: 'ri-flashlight-line', label: '电力间接排放', desc: '工厂用电量及电网排放因子实时核算' },
    { icon: 'ri-temp-hot-line', label: '工业过程排放', desc: '化工、建材等工业过程温室气体排放' },
    { icon: 'ri-truck-line', label: '物流运输排放', desc: '原材料运输及产品配送碳排放追踪' },
    { icon: 'ri-drop-line', label: '废水处理排放', desc: '污水处理过程 CH₄、N₂O 排放核算' },
    { icon: 'ri-recycle-line', label: '废弃物处置', desc: '固废填埋、焚烧等处置方式碳排放' },
  ],
  flowSteps: [
    {
      step: '01',
      icon: 'ri-database-2-line',
      title: '工厂数据自动采集',
      desc: 'IoT 传感器 + 能源表计实时采集，覆盖全厂区所有碳排放源',
      color: 'from-firmai-green/20 to-firmai-green/5',
      borderColor: 'border-firmai-green/30',
    },
    {
      step: '02',
      icon: 'ri-cpu-line',
      title: 'AI 标准化处理',
      desc: '按 CCER 方法学自动核算，自动完成数据清洗、异常检测与校验',
      color: 'from-amber-500/20 to-amber-500/5',
      borderColor: 'border-amber-500/30',
    },
    {
      step: '03',
      icon: 'ri-file-list-3-line',
      title: '合规报告生成',
      desc: '一键生成符合 CCER 格式要求的碳排放报告、监测计划和核查文件',
      color: 'from-firmai-blue/20 to-firmai-blue/5',
      borderColor: 'border-firmai-blue/30',
    },
    {
      step: '04',
      icon: 'ri-upload-cloud-2-line',
      title: '系统直连提交',
      desc: 'API 直连全国温室气体自愿减排注册登记系统，自动化完成数据申报',
      color: 'from-rose-500/20 to-rose-500/5',
      borderColor: 'border-rose-500/30',
    },
  ],
  standards: [
    'GB/T 32150 工业企业温室气体核算',
    'ISO 14064 温室气体核查标准',
    'CCER 方法学 CM-001/CM-002',
    '国家发改委碳排放核算指南',
    'SBTi 科学碳目标认定',
    'GHG Protocol 温室气体议定书',
  ],
};

export const aiProcessSteps = [
  {
    step: '01',
    title: '数据采集',
    description: '毫秒级实时采集设备运行数据，覆盖电、水、气、热等多能源类型',
    icon: 'ri-database-2-line',
  },
  {
    step: '02',
    title: 'AI 分析建模',
    description: '深度学习模型自动识别能耗模式，构建预测与优化算法',
    icon: 'ri-cpu-line',
  },
  {
    step: '03',
    title: '智能优化执行',
    description: 'AI 自动生成优化方案并推送执行，实现闭环管理',
    icon: 'ri-rocket-line',
  },
  {
    step: '04',
    title: '持续迭代进化',
    description: '模型持续学习企业数据，优化效果随时间不断提升',
    icon: 'ri-refresh-line',
  },
];